// Turn a themed post/prayer/book-club message into a shareable PNG.
// Uses the Web Share API where supported (mobile) with a download fallback.
// The rendered off-screen card includes the JJU logo + a "Join us" footer so
// every shared image doubles as an organic invite to the community.

import { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { Send, Download, X, Loader2 } from "lucide-react";
import { toPng } from "html-to-image";
import { getPostTheme } from "@/lib/postThemes";
import { LOGO_URL } from "@/lib/branding";

const SITE_URL = "https://jesusjustus.us";

// Load the JJU logo as a data URL once so html-to-image doesn't hit CORS on
// the customer-assets domain. We keep this in module scope to avoid refetching.
let _logoDataUrl = null;
async function getLogoDataUrl() {
  if (_logoDataUrl) return _logoDataUrl;
  try {
    const res = await fetch(LOGO_URL, { mode: "cors" });
    const blob = await res.blob();
    _logoDataUrl = await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.readAsDataURL(blob);
    });
    return _logoDataUrl;
  } catch {
    return null;
  }
}

export function ShareCardDialog({ open, onClose, content, background, authorName, kind = "post" }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [logo, setLogo] = useState(null);
  const theme = getPostTheme(background);

  // Kind-specific copy for the tiny footer label above the site url.
  const kindLabel = { post: "A moment from Jesus Just Us", prayer: "A prayer from Jesus Just Us", club: "From our Book Club" }[kind] || "Jesus Just Us";
  const fileName = `jesus-just-us-${kind}-${Date.now()}.png`;

  // Load logo once dialog opens so it's baked into the PNG.
  useEffect(() => {
    if (open && logo === null) {
      getLogoDataUrl().then((d) => setLogo(d || ""));
    }
  }, [open, logo]);

  const buildPng = async () => {
    if (!cardRef.current) return null;
    // 1080x1350 is a safe Instagram-story friendly ratio; html-to-image handles
    // the internal scale automatically because we render at CSS pixel sizes.
    // skipFonts avoids noisy CORS SecurityError logs from external font CSS.
    return toPng(cardRef.current, {
      cacheBust: true,
      pixelRatio: 2,
      backgroundColor: "#FDFBF7",
      skipFonts: true,
    });
  };

  const doShare = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const dataUrl = await buildPng();
      if (!dataUrl) throw new Error("Could not render image");
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], fileName, { type: "image/png" });

      // Prefer the Web Share API on mobile for a native share sheet
      // (Messages, WhatsApp, Instagram, etc.). It requires a user gesture,
      // which this click provides.
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: kindLabel,
          text: `${kindLabel} — join us at ${SITE_URL}`,
        });
        toast.success("Shared");
      } else {
        // Fallback: download so the member can attach it wherever they want.
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        toast.success("Image saved — send it anywhere");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Could not create image");
    } finally {
      setBusy(false);
    }
  };

  const doDownload = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const dataUrl = await buildPng();
      if (!dataUrl) throw new Error("no image");
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("Image saved");
    } catch {
      toast.error("Could not save image");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
      data-testid="share-card-dialog"
    >
      <div
        className="jj-card p-6 max-w-md w-full relative"
        style={{ background: "#FFFFFF" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-[var(--jj-surface)] transition-colors"
          aria-label="Close"
          data-testid="share-card-close"
        >
          <X className="h-4 w-4" strokeWidth={1.6} />
        </button>
        <div className="overline text-burgundy">Share this moment</div>
        <h3 className="font-serif text-2xl text-jjblue mt-1">Send it beyond the walls</h3>
        <p className="text-sm text-[var(--jj-muted)] mt-1">
          A JJU-branded card, ready for iMessage, WhatsApp or your story.
        </p>

        {/* Off-screen card that html-to-image snapshots. */}
        <div className="mt-5 rounded-2xl overflow-hidden border border-[var(--jj-border)] shadow-sm">
          <div
            ref={cardRef}
            className="p-8 flex flex-col justify-between"
            style={{
              background: theme.background,
              color: theme.text,
              width: "540px",
              minHeight: "540px",
              maxWidth: "100%",
            }}
            data-testid="share-card-preview"
          >
            <div className="flex items-center justify-between text-[13px]" style={{ color: theme.text, opacity: 0.85 }}>
              <span style={{ fontWeight: 500 }}>{authorName || "A member"}</span>
              <span>{kindLabel}</span>
            </div>
            <p
              className="font-serif whitespace-pre-wrap"
              style={{ color: theme.text, fontSize: "26px", lineHeight: 1.35, textAlign: "center", padding: "24px 4px" }}
            >
              {content}
            </p>
            <div className="flex items-center justify-between" style={{ color: theme.text }}>
              <div className="flex items-center gap-2">
                {logo && (
                  <img
                    src={logo}
                    alt=""
                    style={{ height: 40, width: 40, objectFit: "contain", borderRadius: 8, background: "rgba(255,255,255,0.14)", padding: 4 }}
                  />
                )}
                <div style={{ lineHeight: 1.1 }}>
                  <div style={{ fontFamily: "serif", fontSize: 18, fontWeight: 500 }}>Jesus Just Us</div>
                  <div style={{ fontSize: 11, opacity: 0.75, textTransform: "uppercase", letterSpacing: "0.14em" }}>
                    jesusjustus.us · Join us
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-5 flex gap-2 justify-end">
          <button
            onClick={doDownload}
            disabled={busy}
            className="btn-secondary !py-2 !px-4 text-sm flex items-center gap-2"
            data-testid="share-card-download"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" strokeWidth={1.6} />}
            Save PNG
          </button>
          <button
            onClick={doShare}
            disabled={busy}
            className="btn-primary !py-2 !px-4 text-sm flex items-center gap-2"
            data-testid="share-card-send"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" strokeWidth={1.6} />}
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
